import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/auth";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiError(err, "Accesso non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen bg-ink text-white" data-testid="admin-login-page">
      <div className="hidden flex-1 flex-col justify-between p-12 lg:flex">
        <a href="/" className="font-display text-3xl font-extrabold tracking-tighter" data-testid="admin-login-logo">
          iogama<span className="text-cobalt">.</span>
        </a>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">Area fondatori</p>
          <h1 className="mt-4 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter lg:text-7xl">
            Preventivi
            <br />
            ricevuti.
          </h1>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/30">Napoli · Solo ingrosso</p>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="flex w-full flex-col justify-center border-l border-white/10 bg-white px-6 py-12 text-ink sm:px-12 lg:max-w-xl"
      >
        <a href="/" className="mb-12 font-display text-2xl font-extrabold tracking-tighter lg:hidden">
          iogama<span className="text-cobalt">.</span>
        </a>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta">Accesso riservato</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tighter">Entra nel pannello</h2>
        <form onSubmit={submit} className="mt-10 space-y-4" data-testid="admin-login-form">
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="h-12 w-full border border-black/15 bg-paper px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
            data-testid="admin-login-email"
          />
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="h-12 w-full border border-black/15 bg-paper px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
            data-testid="admin-login-password"
          />
          {error && (
            <p className="border border-terracotta/40 bg-terracotta/5 px-4 py-3 font-mono text-xs text-terracotta" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex h-12 w-full items-center justify-center gap-2 bg-ink font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-cobalt disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            <Lock className="h-3.5 w-3.5" />
            {loading ? "Accesso…" : "Accedi"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </form>
        <a href="/" className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 transition-colors hover:text-ink" data-testid="admin-login-back">
          ← Torna al sito
        </a>
      </motion.div>
    </div>
  );
}
